import { useContext } from 'react'
import PropTypes from 'prop-types'
import { Waypoint } from 'react-waypoint'
import { FormattedMessage } from 'react-intl'

import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Constants
import { ScrolledIntoView, EVENTS_LABELS } from '../../../../constants/trackingEvents'

// Components
import { Format, If, Unless, Lock, Text } from '../../..'
import { LockableInlineCurrencyText } from '../../CopyHelpers'
import PieChart from '../../PieChart'
import YourLoanSentence from '../../../homeowners/YourLoanSentence'

// Helpers
import { oneTimeTagEventDispatcher } from '../../../../helpers/tagManager'
import { useAmplitudeDebounceSetters } from '../../../../hooks/tracking'

// Styles
import styles from './PaidSoFar.module.scss'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.PaidSoFarSection,
})

const propTypes = {
  withCurrentHomeData: PropTypes.bool,
  expand: PropTypes.func,
  expandCheckSection: PropTypes.bool,
  interest: PropTypes.number,
  interestRatio: PropTypes.number,
  loans: PropTypes.array,
  markLoans: PropTypes.func,
  principal: PropTypes.number,
  principalRatio: PropTypes.number,
  principalDiff: PropTypes.number,
  openLoans: PropTypes.func,
  shouldCheckNumbers: PropTypes.bool,
  emptyState: PropTypes.bool,
}

const PaidSoFar = ({
  withCurrentHomeData,
  interest,
  interestRatio,
  principal,
  principalRatio,
  principalDiff,
  shouldCheckNumbers,
  emptyState,
}) => {
  const { ampli } = useContext(AmplitudeContext)

  const [trackModuleViewed] = useAmplitudeDebounceSetters(() =>
    ampli?.moduleViewed({
      module_name: AmplitudeEventConstants.PRINCIPAL_INTEREST,
    })
  )

  const handleTrackModuleViewed = () => {
    scrollIntoView()
    trackModuleViewed()
  }
  return (
    <>
      <If condition={emptyState}>
        <Text
          variant='neutralCool'
          variantCount='900'
          align='center'
          className={styles.noInterestYet}
          size='s'
          data-qa='paidSoFar.unverifiedHeadline'
        >
          <FormattedMessage
            id='Homeowner.interest.noInterestYet'
            defaultMessage='Confirming your loan data is the first step towards smart homeownership.'
          />
        </Text>
      </If>
      <If condition={!emptyState}>
        <div data-qa='paidSoFar.body'>
          <div className={styles.chartContainer}>
            <div className={styles.leftFact}>
              <Text
                element='h3'
                size='l'
                weight='light'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='Homeowner.interest.towardsPrincipal'
                  defaultMessage='Towards Principal'
                />
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                variant='success'
                element='div'
                data-qa='paidSoFar.principal'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={principal} />
                </Lock>
              </Text>
              <Text variant='neutralCool' variantCount='700' size='xs'>
                <Unless condition={principalDiff > 0}>
                  <FormattedMessage
                    id='Homeowner.interest.towardsPrincipal.explain'
                    defaultMessage='How much of your loan you have repaid.'
                  />
                </Unless>
                <If condition={principalDiff > 0}>
                  <FormattedMessage
                    id='Homeowner.interest.towardsPrincipal.explainWithNumbers'
                    defaultMessage="{originalPrincipal} + {principalDiff} extra you've paid ahead of time."
                    values={{
                      originalPrincipal: LockableInlineCurrencyText({
                        currency: principal - principalDiff,
                        variant: 'primary',
                        locked: !withCurrentHomeData,
                      }),
                      principalDiff: LockableInlineCurrencyText({
                        currency: principalDiff,
                        variant: 'success',
                        locked: !withCurrentHomeData,
                      }),
                    }}
                  />
                </If>
              </Text>
            </div>

            <div className={styles.chart}>
              <PieChart
                slices={[
                  {
                    value: interestRatio,
                    key: 'interestRatio',
                    className: styles.interestSlice,
                  },
                  {
                    offset: true,
                    value: principalRatio,
                    key: 'principalRatio',
                    className: styles.principalSlice,
                  },
                ]}
              />
            </div>

            <div className={styles.rightFact}>
              <Text
                element='h3'
                size='l'
                weight='light'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='Homeowner.interest.towardsInterest'
                  defaultMessage='Towards Interest'
                />
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                variant='danger'
                element='div'
                data-qa='paidSoFar.interest'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={interest} />
                </Lock>
              </Text>
              <Text variant='neutralCool' variantCount='700' size='xs'>
                <FormattedMessage
                  id='Homeowner.interest.towardsInterest.explain'
                  defaultMessage='What the bank charges you for your loan.'
                />
              </Text>
            </div>
          </div>
          <If condition={!shouldCheckNumbers && withCurrentHomeData}>
            <Text
              size='xs'
              variant='neutralCool'
              variantCount='700'
              className={styles.loanSentence}
              data-qa='paidSoFar.descriptor'
            >
              <YourLoanSentence />
            </Text>
          </If>
        </div>
      </If>

      <Waypoint onEnter={handleTrackModuleViewed} bottomOffset='25%'>
        <div />
      </Waypoint>
    </>
  )
}

PaidSoFar.propTypes = propTypes

export default PaidSoFar
